import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { AppDispatch, RootState } from '../store/store'
import { appActions } from '../store/slices/appSlice'
import { ConfigProvider, notification } from 'antd'
import { darkTheme, lightTheme } from './appTheme'

export type ObjectList = { [key: string]: any }

// Modal list (enum replacement)
export const ModalList = {} as const
export type ModalListType = (typeof ModalList)[keyof typeof ModalList]

export type AntToast = {
  message: ReactNode
  description?: string
  type?: 'success' | 'info' | 'warning' | 'error'
}

export type AppContextProps = {
  isModalVisible: ObjectList
  isDarkMode: boolean
  showModal: (name: ModalListType) => void
  closeModal: (name: ModalListType) => void
  addToast: (toast: AntToast) => void
  setColorMode: (dark: boolean) => void
}

export const AppContext = createContext<AppContextProps>({
  isModalVisible: {},
  isDarkMode: false,
  addToast: () => null,
  showModal: () => null,
  closeModal: () => null,
  setColorMode: () => null,
})

export const useAppContext = () => useContext(AppContext)

export const BaseProvider = ({ children }: { children: React.ReactNode }) => {
  const [isModalVisible, setIsModalVisible] = useState<ObjectList>({})
  const { isDarkMode } = useSelector((state: RootState) => state.app)
  const dispatch = useDispatch<AppDispatch>()

  const [api, contextHolder] = notification.useNotification()

  const showModal = (name: ModalListType): void => setIsModalVisible({ ...isModalVisible, [name]: true })

  const closeModal = (name: ModalListType): void => {
    delete isModalVisible[name]
    setIsModalVisible({ ...isModalVisible })
  }

  const addToast = (toast: AntToast): void => {
    api[toast.type ?? 'info']({
      title: toast.message,
      description: toast.description,
      duration: 6,
    })
  }

  const setColorMode = (dark: boolean): void => {
    dispatch(appActions.setDarkMode(dark))
  }
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark-mode')
    } else {
      document.documentElement.classList.remove('dark-mode')
    }
  }, [isDarkMode])

  return (
    <AppContext.Provider
      value={{
        isModalVisible,
        isDarkMode,
        addToast,
        showModal,
        closeModal,
        setColorMode,
      }}>
      <ConfigProvider theme={isDarkMode ? darkTheme : lightTheme}>
        {contextHolder}
        {children}
      </ConfigProvider>
    </AppContext.Provider>
  )
}
